import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import { STATUS } from "../../../theme/tokens";
import type { PbjTerminatedEmployeeRow } from "../../../utils/models";
import { usePbjTerminated } from "../hooks/usePbjAudit";
import type { WithBuilding } from "../network/buildingQueries";
import { useBuildingGridParts, useWithBuildingFacet } from "../network/useBuildingGridParts";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { flagFacet, monthFacet, textFacet } from "../pbjFacets";
import { boldNameColumn, hoursColumn, monospaceIdColumn, sectionFrameHeight } from "../pbjGridCells";
import { formatAuditDate, toAuditDate } from "../pbjGridFormat";
import { pbjSortOptions } from "../pbjGridSorting";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — fold in the array index so equal (emp, termination-date) rows never collide. */
type TerminatedGridRow = WithBuilding<PbjTerminatedEmployeeRow> & { __rowId: string };

const WORKED_AFTER_TERM = "worked_after_term";

/** Last shift after the term date — the date or the punches are wrong, and CMS sees both. */
const workedAfterTermination = (r: TerminatedGridRow): boolean => {
	const last = toAuditDate(r.lastShiftWorked);
	const term = toAuditDate(r.terminationDate);
	return last != null && term != null && last.startOf("day") > term.startOf("day");
};

/**
 * Terminated employees audit — staff terminated within (or before) the window with their worked hours.
 * Migrated to the canonical SystemDataGrid (opaque pinned header, skeleton loader, internal scroll).
 */
const COLUMN_FACET: Readonly<Record<string, string>> = { jobDescription: "job", terminationDate: "term_month" };

export const TerminatedTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjTerminated(startDate, endDate);
	const building = useBuildingGridParts<TerminatedGridRow>();

	const rows = useMemo<TerminatedGridRow[]>(
		() => (data ?? []).map((row, index) => ({ ...row, __rowId: `${index}|${row.buildingId}|${row.empId}|${String(row.terminationDate)}` })),
		[data],
	);

	const columns: GridColDef<TerminatedGridRow>[] = useMemo(
		() => [
			...building.columns,
			monospaceIdColumn<TerminatedGridRow>("empId", t("pbj_model.audit.terminated.cols.id")),
			boldNameColumn<TerminatedGridRow>("name", t("pbj_model.audit.terminated.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.terminated.cols.job"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string | null) => value || "—",
			},
			{
				field: "terminationDate",
				headerName: t("pbj_model.audit.terminated.cols.term_date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<TerminatedGridRow>("totalHours", t("pbj_model.audit.terminated.cols.total_hours"), { minWidth: 170 }),
			{
				field: "lastShiftWorked",
				headerName: t("pbj_model.audit.terminated.cols.last_shift"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
		],
		[t, building.columns],
	);

	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<TerminatedGridRow>[]>(
		() => [
			...building.facets,
			{
				dimension: flagFacet<TerminatedGridRow>("review", { [WORKED_AFTER_TERM]: workedAfterTermination }),
				title: t("pbj_model.filter.needs_review"),
				getLabel: () => t("pbj_model.audit.terminated.filter.worked_after_term"),
				colorDot: STATUS.WARNING.main,
				order: [WORKED_AFTER_TERM],
			},
			{
				dimension: textFacet<TerminatedGridRow>("job", (r) => r.jobDescription),
				title: t("pbj_model.audit.terminated.cols.job"),
				getLabel: labels.raw,
				searchable: true,
			},
			{
				dimension: monthFacet<TerminatedGridRow>("term_month", (r) => r.terminationDate),
				title: t("pbj_model.audit.terminated.filter.term_month"),
				getLabel: labels.month,
				order: "key",
			},
		],
		[t, labels, building.facets],
	);
	const filters = useGridFacetFilters(rows, facets, building.sharedGroups);
	const railHandoff = useFilterRailHandoff(filters.highlight, useWithBuildingFacet(COLUMN_FACET), t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<TerminatedGridRow>([
				...building.sortFields,
				{ field: "empId", label: t("pbj_model.audit.terminated.cols.id") },
				{ field: "name", label: t("pbj_model.audit.terminated.cols.name") },
				{ field: "jobDescription", label: t("pbj_model.audit.terminated.cols.job") },
				{ field: "terminationDate", label: t("pbj_model.audit.terminated.cols.term_date") },
				{ field: "totalHours", label: t("pbj_model.audit.terminated.cols.total_hours") },
				{ field: "lastShiftWorked", label: t("pbj_model.audit.terminated.cols.last_shift") },
			]),
		[t, building.sortFields],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.terminated.section.title")}
			description={t("pbj_model.audit.terminated.section.subtitle")}
			dataTestId="pbj-terminated-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<FilterRailFrame
				flat
				height={sectionFrameHeight(rows.length)}
				dataTestId="pbj-terminated-filters-frame"
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-terminated-filters"
					/>
				}
			>
				<SystemDataGrid<TerminatedGridRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(r) => r.__rowId}
					searchableColumns={["empId", "name", "jobDescription"]}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					exportOptions={{ fileName: t("pbj_model.audit.terminated.section.title") }}
					searchPlaceholder={t("pbj_model.audit.terminated.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.terminated.failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.audit.terminated.empty")} dataTestId="pbj-model-audit-terminated-empty" />}
							dataTestId="pbj-model-audit-terminated-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-model-audit-terminated"
				/>
			</FilterRailFrame>
		</SettingsSection>
	);
};
